import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { Skeleton } from '@mui/material'
import { useNavBlocker } from 'commons/customHooks/useNavBlocker'
import { decimalMask } from 'commons/utils/mask'
import { ensureNonNegative } from 'commons/utils/number'
import { convertBooleanApiValue } from 'commons/utils/string'
import { Card, type HeadItemType, Input, LoadingModal, Table, Tooltip } from 'components'
import { TypesValidations } from 'constants/enums'
import type { ProductContextState } from 'container/ProductPage'
import BodyPostProduct from 'container/ProductPage/utils/BodyPostProduct'
import { t } from 'i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as DraftOV from 'services/draft'
import type { MaterialByIdResponse } from 'store/ProductContext'
import type { Color, SizeDescription } from 'store/ProductContext/productContextTypes'
import { setConfirmedNavigation, setShowPrompt } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import ItemSize from '../ItemSize'
import ProductDetail from '../ProductDetail'
import ProductFooter from '../ProductFooter'
import SizesTooltip from '../SizesTooltip'
import Subtotal from './Subtotal'
import * as S from './styles'

interface ProductItemsProps {
  materialSelected: MaterialByIdResponse
  convertAmount: number
  defaultSizes: Record<string, string | string[] | boolean | Record<string, number>>
}

const ITEMS_TO_SHOW = 10

function ProductItems({ materialSelected, convertAmount, defaultSizes }: ProductItemsProps) {
  const {
    loadingMaterialShare,
    materialShares: { colors: colorShares },
  } = useSelector((state) => state.fairShare)
  const { filter, matrixStock, loadingStock, defaultParams } = useSelector(
    (state) => state.products,
  )
  const methods = useFormContext()
  const { getValues, setValue, formState } = methods
  const dispatch = useDispatch()
  const { draftBody, records } = useSelector((stateSalesOrder) => stateSalesOrder.salesOrder)
  const { showDetailedStock } = useSelector((state) => state.structure)
  const { orderNumber } = useParams()
  const navigate = useNavigate()

  const { isDirty } = formState
  useNavBlocker(isDirty)

  const [loading, setLoading] = useState(false)
  const [loadedItems, setLoadedItems] = useState<Color[]>([])
  const [itemsToShow, setItemsToShow] = useState(ITEMS_TO_SHOW)

  const memorizedMaterials = useMemo(() => materialSelected.cores, [materialSelected])

  useEffect(() => {
    if (loadedItems.length < memorizedMaterials.length) {
      setLoadedItems((prevLoadedItems) => {
        const itemsToAdd = memorizedMaterials.slice(prevLoadedItems.length, itemsToShow)
        return [...prevLoadedItems, ...itemsToAdd]
      })
    }

    setLoading(false)
  }, [itemsToShow, memorizedMaterials])

  const handleScroll = () => {
    setLoading(true)
    setItemsToShow((prev) => prev + ITEMS_TO_SHOW)
  }

  const productColors = useMemo(
    () => memorizedMaterials.map((item) => item.ds_cor),
    [memorizedMaterials],
  )

  const hideSkuStock = useMemo(
    () => /m|c/i.test(materialSelected?.tp_estq_exce),
    [materialSelected],
  )

  const isColorStock = useMemo(
    () =>
      /c/i.test(materialSelected?.tp_estq_exce) &&
      materialSelected?.fl_pedi_antp !== TypesValidations.YES,
    [materialSelected],
  )

  const isFairShareOffice = useMemo(() => {
    return convertBooleanApiValue(draftBody.fl_utlz_fair_shar)
  }, [draftBody])

  const isOutOfLine = useMemo(() => {
    return convertBooleanApiValue(defaultParams.fl_matr_fora_linh)
  }, [defaultParams])

  const materialDiscount = useMemo(() => {
    const selectedItem = records.find(
      (record) => record.nr_item_pedi_rasc === materialSelected.nr_item_pedi_rasc,
    )

    if (!selectedItem?.pc_dsct) return draftBody.pc_dsct

    return +selectedItem.pc_dsct
  }, [materialSelected])

  const watchedMixedBox: boolean = useWatch({
    control: methods.control,
    name: 'fl_caix_grad',
  })

  const navigateBack = () => navigate(-1)
  const handleSaveWithOrderNumber = async (state: ProductContextState) => {
    let result = false
    setLoading(true)

    DraftOV.postMaintainItem(
      BodyPostProduct({
        defaultSizes,
        draftBody,
        filter,
        materialDiscount,
        materialSelected,
        state,
      }),
    )
      .then((response) => {
        dispatch(setConfirmedNavigation(true))

        if (response && response.nr_pedi_rasc) {
          result = true
          navigateBack()
          dispatch(setShowPrompt(false))
        }
      })
      .catch(() => {
        result = false
      })
      .finally(() => setLoading(false))

    return result
  }
  const handleSave = async (state: ProductContextState) => {
    let result = false
    setLoading(true)

    if (orderNumber) {
      const request = await handleSaveWithOrderNumber(state)
      result = request
    }

    return result
  }

  const getFilter = useCallback(
    (color: Color, arr: string[]) => {
      if (arr.length < 1) {
        return true
      }
      // TODO: Refactor to use cd_cor instead of ds_cor
      return arr.includes(color.ds_cor.toLowerCase())
    },
    [filter],
  )

  const sizesSet = useMemo(() => {
    const sizes = memorizedMaterials.flatMap((cor) =>
      cor.tamanhos.flatMap((tamanho) => {
        return { description: tamanho.componentes, size: tamanho.cd_tamh }
      }),
    )

    sizes.sort((a, b) => a.size.localeCompare(b.size))

    const memoSet = new Set<string>()
    sizes.forEach((size) => memoSet.add(JSON.stringify(size)))
    return memoSet
  }, [materialSelected])

  const isCustomBox = getValues('fl_caix_grad')

  const headData = (): HeadItemType[] => {
    const sizes = [...sizesSet].map((size) => {
      const object: { size: string; description: SizeDescription[] } = JSON.parse(size)

      return {
        columnName: object.size,
        label: (
          <S.LabelStyled key={`${object.size}`}>
            {object.size}{' '}
            {!!object.description.length && <SizesTooltip description={object.description} />}
          </S.LabelStyled>
        ),
      }
    })

    const tableHead: HeadItemType[] = [
      {
        columnName: 'color',
        label: t('common.color'),
      },
      ...sizes,
    ]

    if (!isCustomBox)
      tableHead.splice(1, 0, {
        columnName: 'typed',
        label: t('common.typed'),
      })

    if (isColorStock)
      tableHead.splice(1, 0, {
        columnName: 'stock',
        label: t('common.stock'),
      })

    if (isFairShareOffice && !isOutOfLine) {
      tableHead.splice(1, 0, {
        columnName: 'fairShare',
        label: t('screens.fairShare.balance'),
      })
    }

    return tableHead
  }

  function noReload(event: FormEvent) {
    event.preventDefault()
  }

  useEffect(() => {
    setLoading(false)
  }, [loadedItems])

  const currentFormValues = Object.entries(getValues()).filter((item) =>
    loadedItems.some((cor) => cor?.ds_cor === item[0]),
  )

  const getRecordsData = useMemo(() => {
    const currentDirtyColors = currentFormValues.reduce(
      (acc, item: [string, Record<string, number>]) => {
        const colorSum = Object.values(item[1]).reduce((colorAcc, value) => colorAcc + value, 0)
        if (colorSum) return [...acc, item[0]]
        return acc
      },
      [] as string[],
    )

    const materials = filter.length ? memorizedMaterials : loadedItems

    const recordsData = materials
      .filter((cor) => getFilter(cor, filter))
      .map((cor) => {
        const colorStock = matrixStock.cores
          ? matrixStock.cores.find((c) => c.codigo === cor.cd_cor)
          : null

        const inputs = [...sizesSet].map((size) => {
          const object: { size: string; description: SizeDescription[] } = JSON.parse(size)

          const foundSize = cor.tamanhos.find((t) => t.cd_tamh === object.size)

          if (foundSize?.cd_tamh) {
            const sizeStock =
              colorStock && colorStock.tamanhos
                ? colorStock.tamanhos.find((size) => size.codigo === foundSize.cd_tamh)
                : null

            const setItemSizeDisabled = (): boolean | undefined => {
              if (materialSelected.fl_digi_caix_vari !== TypesValidations.YES) {
                return false
              }

              const dirtyFieldsByColor = currentDirtyColors?.filter((dirtyColors) =>
                productColors.includes(dirtyColors),
              )

              setValue('dirtyFieldsByColor', dirtyFieldsByColor)
              setValue('disabled_fl_caix_grad', dirtyFieldsByColor.length > 1)

              if (!watchedMixedBox || !dirtyFieldsByColor.length) return false

              return dirtyFieldsByColor.length >= 2 && dirtyFieldsByColor[0].includes(cor.ds_cor)
            }

            return (
              <S.ItemBox key={`${cor.ds_cor}-${foundSize?.cd_tamh}`}>
                <ItemSize
                  colorName={cor.ds_cor}
                  disabled={setItemSizeDisabled()}
                  size={foundSize?.cd_tamh}
                />
                {showDetailedStock && !hideSkuStock && (
                  <>
                    {loadingStock ? (
                      <Skeleton animation="wave" height={17} width={30} />
                    ) : (
                      <span>
                        {sizeStock ? decimalMask(ensureNonNegative(sizeStock.volumeDisponivel)) : 0}
                      </span>
                    )}
                  </>
                )}
              </S.ItemBox>
            )
          }

          return (
            <S.ItemBox key={`${cor.ds_cor}-${object.size}-not-exist`}>
              <Input
                disabled
                height={30}
                InputProps={{
                  inputProps: { maxLength: 6 },
                  placeholder: '',
                }}
                value=""
                width={80}
              />
            </S.ItemBox>
          )
        })

        const data = {
          content: [
            <S.ColorBor key={`${cor.cd_cor}-${cor.ds_cor}-sub`}>
              <S.ColorTitleStyled>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <S.ToolTipBox>
                      <span id="title">
                        {cor.cd_cor} - {cor.ds_cor}
                      </span>
                    </S.ToolTipBox>
                  }
                >
                  <S.InfoOutlineIconStyled />
                </Tooltip>
                <span id="title">{cor.cd_cor}</span>
              </S.ColorTitleStyled>
              {cor.fl_matr_estt === TypesValidations.YES && <span id="strategic">Estratégico</span>}
              <S.ColorDescription>{cor.ds_cor}</S.ColorDescription>
            </S.ColorBor>,
            ...inputs,
          ],
          id: `${cor.ds_cor}-${cor.cd_cor}`,
        }

        if (!isCustomBox) data.content.splice(1, 0, <Subtotal colorName={cor.ds_cor} />)

        if (isColorStock) {
          data.content.splice(
            1,
            0,
            <>
              {loadingStock ? (
                <Skeleton animation="wave" height={22} width={50} />
              ) : (
                <S.ColorStock>
                  {typeof colorStock?.totalDisponivel === 'number'
                    ? decimalMask(ensureNonNegative(colorStock?.totalDisponivel))
                    : '-'}
                </S.ColorStock>
              )}
            </>,
          )
        }

        if (isFairShareOffice && !isOutOfLine) {
          const colorShare = colorShares?.find((c) => c.colorCode === cor.cd_cor)
          data.content.splice(
            1,
            0,
            <>
              {loadingMaterialShare ? (
                <Skeleton animation="wave" height={22} width={50} />
              ) : (
                <S.ColorStock>
                  {typeof colorShare?.availableQuantity === 'number'
                    ? decimalMask(ensureNonNegative(colorShare?.availableQuantity))
                    : '-'}
                </S.ColorStock>
              )}
            </>,
          )
        }

        return data
      })

    return recordsData
  }, [
    filter,
    sizesSet,
    getValues,
    showDetailedStock,
    watchedMixedBox,
    productColors,
    loadedItems,
    loadingStock,
    matrixStock,
    loadingMaterialShare,
    colorShares,
  ])

  return (
    <form onSubmit={noReload}>
      {loading && <LoadingModal />}
      <Card>
        <S.BoxColumn>
          <ProductDetail convertAmount={convertAmount} />
          <Table
            bodyData={getRecordsData}
            dense
            distributeWidth={false}
            fixedFirstCol
            fixedHead
            headData={headData()}
            inputAlign={watchedMixedBox ? 'left' : 'center'}
            onScroll={handleScroll}
            rowHeight={showDetailedStock ? 70 : 50}
          />
        </S.BoxColumn>
      </Card>
      <ProductFooter defaultSizes={defaultSizes} isLoading={loading} onSave={handleSave} />
    </form>
  )
}

export default ProductItems
